
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { OrganizationForm } from './OrganizationForm';
import { getOrganizations, deleteOrganization } from '@/services/organizationService';
import { Building, Plus, Edit, Trash2 } from 'lucide-react';
import { Organization } from '@/types/flight';

export function OrganizationManagement() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const data = await getOrganizations();
      setOrganizations(data);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error loading organizations",
        description: error.message || "Failed to load organizations.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleOrganizationSubmit = () => {
    setIsDialogOpen(false);
    setSelectedOrganization(null);
    fetchOrganizations();
  };

  const handleDeleteOrganization = async (id: string) => {
    if (!confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteOrganization(id);
      toast({
        title: "Organization deleted",
        description: "The organization has been deleted successfully.",
      });
      fetchOrganizations();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error deleting organization",
        description: error.message || "Failed to delete organization.",
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader style={{ backgroundColor: 'orange' }}>
          <CardTitle>Organization Management</CardTitle>
          <CardDescription>Loading organizations...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Organization Management
          </span>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setSelectedOrganization(null)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {selectedOrganization ? 'Edit Organization' : 'Create New Organization'}
                </DialogTitle>
              </DialogHeader>
              <OrganizationForm 
                organization={selectedOrganization} 
                onSubmit={handleOrganizationSubmit}
              />
            </DialogContent>
          </Dialog>
        </CardTitle>
        <CardDescription>
          Manage organizations and their settings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {organizations.map((organization) => (
            <div key={organization.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-medium">{organization.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Code: {organization.code}
                    </p>
                  </div>
                  <Badge variant={organization.is_active ? "default" : "secondary"}>
                    {organization.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
              <div className="flex gap-2">
                <Dialog open={isDialogOpen && selectedOrganization?.id === organization.id} onOpenChange={(open) => {
                  setIsDialogOpen(open);
                  if (!open) setSelectedOrganization(null);
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedOrganization(organization)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Edit Organization</DialogTitle>
                    </DialogHeader>
                    <OrganizationForm 
                      organization={selectedOrganization} 
                      onSubmit={handleOrganizationSubmit}
                    />
                  </DialogContent>
                </Dialog>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDeleteOrganization(organization.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          
          {organizations.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No organizations found. Create your first organization to get started.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
